import {
  Component,
  EventEmitter,
  Output,
  computed,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ComoService } from '@core/services/como/como.service';
import { UserService } from '@core/services/user/user.service';
import { takeUntil, tap } from 'rxjs/operators';
import { FidelityType } from '../fidelity-type.enum';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ZerosixError } from '@core/models/zerosix.model';
import { ZEROSIX_ERRORS } from '@config/values/zerosix-errors.values';
import { getAllCountries } from '@shared/components/input-country-phone/config/countries.config';

/** COMO API: phone/email already registered — switch to attach/login flow. */
const COMO_ACCOUNT_ALREADY_EXISTS_CODE = 103;

@Component({
  selector: 'app-kiosk-register-step',
  template: `
    <div class="flex items-center justify-between">
      <h1
        class="pb-5 font-brand text-28px font-bold text-neutral-800"
        translate="como.link-account.header"
      ></h1>
      <app-rounded-icon-button
        icon="icon-cross"
        (onClick)="onClose.emit()"
      ></app-rounded-icon-button>
    </div>

    <form>
      <label
        for="phone"
        class="mb-1 block font-base text-sm font-normal leading-5 text-neutral-600"
        translate="signup.phone"
      ></label>

      <app-kiosk-input-country-phone-atom
        id="phone"
        [phone]="phone()"
        [useLocalFormat]="useLocalFormat()"
        [defaultRegionCode]="defaultRegionCode()"
        [helpTranslateKey]="fidelityType() + '.help-phone-format'"
        (updatePhone)="phone.set($any($event))"
        (keydown)="handleKeyDown($event)"
      />

      <div class="hidden kiosk:block">
        <app-numeric-keypad-atom
          (numberClick)="onKeypadNumberClick($event)"
          (deleteClick)="onKeypadDeleteClick()"
        ></app-numeric-keypad-atom>
      </div>

      @if (errorMessage) {
        <div
          class="mt-2 font-base text-xs font-normal text-error-700"
          [translate]="errorMessage"
        ></div>
      }

      @if (fidelityType() === FidelityType.COMO) {
        <div
          class="mt-6 flex items-start"
          [class.kiosk-consent-attention]="consentAttention()"
        >
          <input
            id="como-marketing-consent"
            type="checkbox"
            class="form-checkbox m-3 h-6 w-6 shrink-0 border text-primary-600"
            [checked]="marketingAccepted()"
            (change)="onMarketingAcceptedChange($event)"
          />
          <label
            for="como-marketing-consent"
            class="mt-3 font-base text-sm leading-5 text-neutral-800"
          >
            {{
              'signup.dood-communication'
                | translate: { marketplaceName$: marketplaceName$ | async }
            }}
          </label>
        </div>
      }

      <div class="mt-10 flex flex-col">
        <app-button-atom
          size="L"
          [label]="fidelityType() + '.validate' | translate"
          [iconUrl]="isValidating ? 'assets/images/spinner.svg' : undefined"
          [disabled]="isValidating || !phone()"
          [lookDisabled]="isComoConsentMissing()"
          [overrideTextColor]="isComoConsentMissing() ? undefined : accentColor()?.font"
          [overrideBgColor]="isComoConsentMissing() ? undefined : accentColor()?.background"
          type="primary"
          [full]="true"
          (click)="onRegister()"
        ></app-button-atom>
      </div>
    </form>
  `,
  styles: [
    `
      @keyframes kiosk-consent-blink {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.15;
        }
      }

      .kiosk-consent-attention {
        animation: kiosk-consent-blink 0.4s ease-in-out 3;
      }
    `,
  ],
  standalone: false,
})
export class KioskRegisterStepComponent extends DestroyerBase {
  @Output() onComplete = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @Output() goToKioskRegisterConfirmationStep = new EventEmitter<void>();
  @Output() goToOtpStep = new EventEmitter<void>();
  @Output() goToAuthenticationStep = new EventEmitter<void>();

  readonly phone = model('');
  readonly useLocalFormat = input<boolean>(false);

  readonly accentColor = input<ContentBuilderFieldColors>();
  readonly fidelityType = input.required<FidelityType>();
  readonly defaultRegionCode = input<string>('fr');

  /** Como marketing consent. Required, left unchecked until the guest ticks it. */
  readonly marketingAccepted = signal(false);
  /** Toggled so the checkbox and its label blink when submit is pressed without consent. */
  readonly consentAttention = signal(false);
  readonly isComoConsentMissing = computed(
    () => this.fidelityType() === FidelityType.COMO && !this.marketingAccepted(),
  );
  protected readonly FidelityType = FidelityType;

  isValidating = false;
  errorMessage?: string;
  private consentAttentionFrame?: ReturnType<typeof setTimeout>;
  private consentAttentionClear?: ReturnType<typeof setTimeout>;

  private readonly zerosixService = inject(ZerosixService);
  private readonly comoService = inject(ComoService);
  private readonly settingsSelector = inject(SettingsStoreSelector);
  private readonly marketplaceSelector = inject(MarketplaceStoreSelector);
  private readonly userService = inject(UserService);

  kioskParameters = this.settingsSelector.kiosk;
  readonly marketplaceName$ = this.marketplaceSelector.selectMarketplaceName;

  onMarketingAcceptedChange(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    this.marketingAccepted.set(input.checked);
    if (input.checked) {
      this.consentAttention.set(false);
    }
  }

  /**
   * Drop the class for one frame, then put it back so a second click restarts the blink.
   */
  private flashMissingConsent(): void {
    clearTimeout(this.consentAttentionFrame);
    clearTimeout(this.consentAttentionClear);
    this.consentAttention.set(false);
    this.consentAttentionFrame = setTimeout(() => {
      this.consentAttention.set(true);
      this.consentAttentionClear = setTimeout(() => this.consentAttention.set(false), 1200);
    });
  }

  override ngOnDestroy(): void {
    clearTimeout(this.consentAttentionFrame);
    clearTimeout(this.consentAttentionClear);
    super.ngOnDestroy();
  }

  public onRegister(): void {
    if (this.isValidating || !this.phone()) {
      return;
    }

    if (this.isComoConsentMissing()) {
      this.flashMissingConsent();
      return;
    }

    switch (this.fidelityType()) {
      case FidelityType.COMO:
        this.registerComo();
        break;
      case FidelityType.ZEROSIX:
        this.registerZerosix();
        break;
      default:
        break;
    }
  }

  private registerComo(): void {
    this.isValidating = true;
    this.errorMessage = undefined;

    this.comoService
      .createComoUser$(this.phone(), false, null, this.marketingAccepted())
      .pipe(
        takeUntil(this._destroyerRef),
        tap(user => this.userService.setUser(user)),
        tap(() => this.goToKioskRegisterConfirmationStep.next()),
      )
      .subscribe({
        error: error => this.handleComoCreateError(error.error),
        complete: () => (this.isValidating = false),
      });
  }

  /**
   * Existing loyalty members hitting "register" get code 103.
   * Fall through to attach-user so they can receive an OTP instead of a dead-end generic error.
   */
  private handleComoCreateError(error: { code?: number } | undefined): void {
    if (error?.code === COMO_ACCOUNT_ALREADY_EXISTS_CODE) {
      this.linkExistingComoAccount();
      return;
    }

    this.isValidating = false;
    this.errorMessage = this.comoService.getErrorMessageFromCode(error?.code as number);
  }

  private linkExistingComoAccount(): void {
    this.comoService
      .linkComoUser$({ phoneOrEmail: this.phone() })
      .pipe(
        takeUntil(this._destroyerRef),
        tap(response => {
          if (response.otp_sent) {
            this.goToOtpStep.next();
          } else {
            this.goToAuthenticationStep.next();
          }
        }),
      )
      .subscribe({
        error: error => {
          this.isValidating = false;
          this.errorMessage = this.comoService.getErrorMessageFromCode(error.error?.code);
        },
        complete: () => (this.isValidating = false),
      });
  }

  private registerZerosix(): void {
    this.isValidating = true;
    this.errorMessage = undefined;

    this.zerosixService
      .createZerosixUser$(this.phone())
      .pipe(
        takeUntil(this._destroyerRef),
        tap(user => this.userService.setUser(user)),
        tap(() => this.goToKioskRegisterConfirmationStep.next()),
      )
      .subscribe({
        error: error => this.handleZerosixErrorResponse(error.error),
        complete: () => (this.isValidating = false),
      });
  }

  private handleZerosixErrorResponse(error: ZerosixError): void {
    this.isValidating = false;

    if (error.class === 'ZerosixException') {
      this.errorMessage = ZEROSIX_ERRORS[error.code];
      return;
    }
    this.errorMessage = 'errors.generic-error';
  }

  onKeypadNumberClick(number: number): void {
    const currentValue = this.phone() || '';
    this.phone.set(currentValue + number.toString());
  }

  onKeypadDeleteClick(): void {
    const currentValue = this.phone();
    const defaultRegionCode = this.defaultRegionCode() || 'fr';

    // Get the default dial code based on the region
    const defaultDialCode = this.getDefaultDialCode(defaultRegionCode);

    // Don't allow deletion if it would remove part of the default dial code
    // or if the current value is already just the dial code
    if (currentValue.length <= defaultDialCode.length || currentValue === defaultDialCode) {
      return;
    }

    this.phone.set(currentValue.slice(0, -1));
  }

  private getDefaultDialCode(regionCode: string): string {
    // Get all countries and create a map from iso2 to dial code
    const countries = getAllCountries();
    const country = countries.find(c => c.iso2.toLowerCase() === regionCode.toLowerCase());

    if (country) {
      return `+${country.dialCode}`;
    }

    // Default to France if region code not found
    return '+33';
  }

  public handleKeyDown(event: KeyboardEvent): void {
    // Prevent default action for Enter key to avoid triggering the first button
    if (event.key === 'Enter') {
      event.preventDefault();
      // Optionally trigger form submission if the form is valid
      if (this.phone()) {
        this.onRegister();
      }
    }
  }
}
